import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  OnChanges,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { Overlay, OverlayRef, ConnectedPosition } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { UploadErrorsTemplateModalComponent } from "../ulpoad-template-modal/upload-errors-template-modal/upload-errors-template-modal.component";
import { TranslateService } from "@ngx-translate/core";

export interface FilterItem {
  id: any;
  label: string;
}

@Component({
  selector: "app-custom-import-multi-select-modal",
  templateUrl: "./custom-import-multi-select-modal.component.html",
  styleUrls: ["./custom-import-multi-select-modal.component.scss"],
  standalone: false
})
export class CustomImportMultiSelectModalComponent implements OnChanges {
  @Input() anchor!: ElementRef<HTMLElement>;
  @Input() options: FilterItem[] = [];
  @Input() selectedItems: FilterItem[] = [];
  @Input() titleKey = "";
  @Input() placeholderText = "";
  @Output() importData = new EventEmitter<{
    selected: FilterItem[];
    file: File;
  }>();
  @Output() clearAll = new EventEmitter<void>();
  @Output() downloadTemplate = new EventEmitter<void>();

  @ViewChild("importTemplate") importTemplate!: any;

  overlayRef?: OverlayRef;
  searchControl = new FormControl("");
  filteredOptions: FilterItem[] = [];
  selectedFileName: string | null = null;
  selectedFile?: File;

  constructor(
    private overlay: Overlay,
    private translateService: TranslateService,
    private viewContainerRef: ViewContainerRef,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.filteredOptions = [...this.options];
    this.searchControl.valueChanges.subscribe((term) => {
      const value = (term ?? "").toLowerCase();
      this.filteredOptions = this.options.filter((option) =>
        option.label.toLowerCase().includes(value)
      );
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["options"]) this.filteredOptions = [...this.options];
    if (changes["selectedItems"])
      this.selectedItems = [...(this.selectedItems || [])];
  }

  open() {
    if (this.overlayRef) {
      this.close();
      return;
    }

    setTimeout(() => {
      const positions: ConnectedPosition[] = [
        {
          originX: "center",
          originY: "bottom",
          overlayX: "center",
          overlayY: "top",
          offsetY: 8
        }
      ];

      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.anchor)
        .withPositions(positions)
        .withPush(false)
        .withFlexibleDimensions(false);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: "cdk-overlay-transparent-backdrop",
        scrollStrategy: this.overlay.scrollStrategies.reposition()
      });

      const portal = new TemplatePortal(
        this.importTemplate,
        this.viewContainerRef
      );
      this.overlayRef.attach(portal);
      this.overlayRef.backdropClick().subscribe(() => this.close());
    });
  }

  close() {
    this.overlayRef?.detach();
    this.overlayRef = undefined;
    this.resetFields();
  }

  resetFields() {
    this.selectedItems = [];
    this.selectedFile = undefined;
    this.selectedFileName = null;
    this.searchControl.reset();
    this.filteredOptions = [...this.options];
  }

  trackById(index: number, item: FilterItem): any {
    return item.id;
  }

  toggleSelection(item: FilterItem) {
    const index = this.selectedItems.findIndex((i) => i.id === item.id);
    index >= 0
      ? this.selectedItems.splice(index, 1)
      : this.selectedItems.push(item);
  }

  isSelected(item: FilterItem): boolean {
    return this.selectedItems.some((i) => i.id === item.id);
  }

  onClear() {
    this.resetFields();
    this.clearAll.emit();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ];
      if (!validTypes.includes(file.type)) {
        input.value = "";
        return;
      }
      this.selectedFile = file;
      this.selectedFileName = file.name;
    }
  }

  async onImport() {
    if (!this.selectedFile || !this.selectedItems.length) return;

    try {
      this.importData.emit({
        selected: this.selectedItems,
        file: this.selectedFile
      });
      this.close();
    } catch (error: any) {
      this.handleUploadError(error);
    }
  }

  handleUploadError(errorResponse: any) {
    const errors = this.formatUploadErrors(errorResponse);

    this.dialog.open(UploadErrorsTemplateModalComponent, {
      width: "700px",
      data: {
        title: "label-import-price-tables",
        subtitle: "price-errors-modal.subtitle",
        columns: [
          { key: "line", label: "upload-template-modal.label-line" },
          {
            key: "description",
            label: "upload-template-modal.label-description"
          }
        ],
        errors
      }
    });
  }

  private formatUploadErrors(res: any) {
    if (Array.isArray(res?.errors)) {
      return res.errors.map((e: any) => ({
        line: e.line ?? 0,
        description:
          e.message ??
          this.translateService.instant("import-export.modal.error-get")
      }));
    }

    if (res?.message) {
      return [{ line: 0, description: res.message }];
    }

    return [
      {
        line: 0,
        description: this.translateService.instant(
          "import-export.modal.error-get"
        )
      }
    ];
  }

  onDownloadTemplate() {
    this.downloadTemplate.emit();
  }
}
